import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from '../contexts/SocketContext';
import type { BetSuccessPayload, CashoutSuccessPayload } from '../types/game.types';
import { soundManager } from './entities/SoundManager';
import FriendsControls from './FriendsControls';
import ActionIDManager from './ActionIDManager';
import { formatMoney, calculateReturn, parseAmountInput, amountInputChars } from '../utils/money';
import { useLiveMultiplier } from '../game/useLiveMultiplier';
import { emitIfConnected } from '../utils/socketEmit';
import { DISPLAY_CONFIG } from '../config/display';
import { SUPPORT_URL } from '../config/legal';

/** The smallest wager the server accepts (gameService.js). */
const MIN_WAGER = 0.01;
const isInsufficientBalance = (message: unknown) => typeof message === 'string' && /insufficient balance/i.test(message);
const isTextField = (target: EventTarget) => target instanceof HTMLInputElement && target.type !== 'checkbox' && target.type !== 'radio';
/** The keypad's Done/Return key just closes the keyboard. */
const closeKeyboardOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => { if (event.key === 'Enter') event.currentTarget.blur(); };
/**
 * How long the bar stays in the panel after a field loses focus. Tapping the
 * wager button blurs the field first; docking at once would move the button
 * out from under the finger before the tap lands.
 */
const REDOCK_DELAY_MS = 350;
/** Decimal keypad with a Done key on phones; parsed with parseAmountInput (comma or point). */
const decimalField = { type: 'text', inputMode: 'decimal', enterKeyHint: 'done', autoComplete: 'off', onKeyDown: closeKeyboardOnEnter } as const;

/**
 * Credits are refilled by the operator only, so a player who has run out is
 * pointed at Support rather than left with a bare "Insufficient balance".
 * Only when they really cannot place the smallest wager: a player who still has
 * credits is told to lower the wager instead (moreThanBalance).
 */
const OutOfCreditsNotice: React.FC = () => (
  <p className="credits-notice" role="status">
    You're out of play credits. Ask <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">Support</a> for a free refill.
  </p>
);

/** A wager above a balance that can still pay for a smaller one. */
const moreThanBalance = (balance: number) => `That's more than your ${formatMoney(balance)} balance. Lower the wager.`;
/** Too few credits for a wager: lower it, or, below the minimum, nothing (the out-of-credits notice shows instead). */
const notEnoughMessage = (balance: number | undefined) => (balance !== undefined && balance >= MIN_WAGER ? moreThanBalance(balance) : '');

interface BettingControlsProps { soloMode: boolean; onSoloModeChange: (solo: boolean) => void; }

const ModeToggle: React.FC<{ soloMode: boolean; locked: boolean; onFriends?: () => void; onChange: (solo: boolean) => void }> = ({ soloMode, locked, onChange, onFriends }) => (
  <div className="mode-control">
    <div className="control-label-row"><span className="control-label">Run mode</span>{locked && <span className="lock-label">Locked this round</span>}</div>
    <div className={`segmented-control${onFriends ? ' has-friends-entry' : ''}`} aria-label="Run mode">
      <button className={soloMode ? 'is-active' : ''} onClick={() => onChange(true)} disabled={locked} aria-pressed={soloMode}>
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3a4 4 0 100 8 4 4 0 000-8zM5 21a7 7 0 0114 0" /></svg>Solo
      </button>
      {onFriends && <button className="friends-plus" aria-label="Play with friends" aria-haspopup="dialog" title="Play with friends" onClick={onFriends}>+</button>}
      <button className={!soloMode ? 'is-active online' : ''} onClick={() => onChange(false)} disabled={locked} aria-pressed={!soloMode}>
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M8 11a4 4 0 100-8 4 4 0 000 8zM16 10a3 3 0 100-6M1 21a7 7 0 0114 0M16 14a6 6 0 017 6" /></svg>Online
      </button>
    </div>
    <p className="control-hint">{soloMode ? 'Your wager and identity stay private.' : 'Race alongside other players.'}</p>
  </div>
);

const BettingControls: React.FC<BettingControlsProps> = ({ soloMode, onSoloModeChange }) => {
  const { socket, player, gameInfo, myBet: contextMyBet, devSimulation, connectionState } = useSocket();
  // The same prediction the canvas draws, so the button agrees with the skier.
  const currentMultiplier = useLiveMultiplier();
  const myBet = contextMyBet ?? { isActive: false, amount: 0, cashedOut: false, cashoutMultiplier: null };
  const [betAmount, setBetAmount] = useState('100');
  const [error, setError] = useState('');
  const [autoCashoutEnabled, setAutoEnabled] = useState(false);
  const [autoCashoutMult, setAutoMult] = useState('2.00');
  const [autoCashoutTriggered, setAutoTriggered] = useState(false);
  // Read by the socket handlers, which are not re-registered on every balance change.
  const balanceRef = useRef<number | undefined>(player?.balance);
  balanceRef.current = player?.balance;
  // While a wager field has the keyboard up, the docked phone action bar
  // returns to the panel so it cannot sit over the field being edited.
  const [editingField, setEditingField] = useState(false);
  const redockTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(redockTimer.current), []);
  const onFieldFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!isTextField(event.target) || !event.currentTarget.contains(event.target)) return;
    window.clearTimeout(redockTimer.current);
    setEditingField(true);
  };
  const onFieldBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!isTextField(event.target)) return;
    window.clearTimeout(redockTimer.current);
    redockTimer.current = window.setTimeout(() => setEditingField(false), REDOCK_DELAY_MS);
  };
  const roundId = gameInfo?.currentGame?.id;
  const reportError = (message: string) => { setError(message); soundManager.playSFX('reject'); };
  useEffect(() => { if (gameInfo?.phase === 'STARTING') { setAutoTriggered(false); setError(''); } }, [gameInfo?.phase]);
  // A new wager amount answers whatever was wrong with the last one.
  useEffect(() => { setError(''); }, [betAmount]);
  useEffect(() => {
    if (!socket) return;
    const onError = (message: string) => {
      setError(isInsufficientBalance(message) ? notEnoughMessage(balanceRef.current) : message);
      soundManager.playSFX('reject');
    };
    const confirm = (name: 'betPlace' | 'cashout', event?: { gameId?: unknown }) => {
      if (devSimulation?.active || !roundId || event?.gameId !== roundId || !player?.id) return;
      soundManager.playConfirmedSFX(name, `${player.id}:${roundId}`);
    };
    const onSuccess = (data: BetSuccessPayload) => { setError(''); confirm('betPlace', data?.bet); };
    const onCashoutSuccess = (data: CashoutSuccessPayload) => {
      setError('');
      setAutoTriggered(data?.cashout?.isAuto === true);
      confirm('cashout', data?.cashout);
    };
    socket.on('game:bet:success', onSuccess);
    socket.on('game:bet:error', onError);
    socket.on('game:cashout:success', onCashoutSuccess);
    socket.on('game:cashout:error', onError);
    return () => { socket.off('game:bet:success', onSuccess); socket.off('game:bet:error', onError); socket.off('game:cashout:success', onCashoutSuccess); socket.off('game:cashout:error', onError); };
  }, [socket, roundId, player?.id, devSimulation?.active]);

  const amount = parseAmountInput(betAmount) || 0;
  // A wager placed while the socket is down would never reach the round engine,
  // so the control is closed rather than failing after the fact.
  const canBet = !devSimulation?.active && connectionState === 'online'
    && gameInfo?.phase === 'STARTING' && !myBet.isActive;
  const isOnline = connectionState === 'online';
  const hasOpenLiveBet = !devSimulation?.active && gameInfo?.phase === 'IN_PROGRESS' && myBet.isActive && !myBet.cashedOut;
  // A cashout tapped while offline must never be queued and delivered on
  // reconnect at a multiplier the player did not choose, so the control closes.
  const canCashout = hasOpenLiveBet && isOnline;
  const returnToSolo = useCallback(() => onSoloModeChange(true), [onSoloModeChange]);
  const modeLocked = gameInfo?.phase === 'LAUNCHING' || gameInfo?.phase === 'IN_PROGRESS' || gameInfo?.phase === 'CRASHED';
  const activeBetAmount = myBet.isActive ? myBet.amount : amount;
  const shownCashoutMultiplier = myBet.cashoutMultiplier ?? currentMultiplier;
  // Out of credits means unable to place even the smallest wager.
  const outOfCredits = !!player && !myBet.isActive && player.balance < MIN_WAGER;

  const handleBet = async () => {
    if (!socket) return reportError('Not connected');
    if (!Number.isFinite(amount) || amount < MIN_WAGER || amount > 10000 || Math.abs(amount * 100 - Math.round(amount * 100)) > 1e-7) return reportError('Wager must be 0.01–10,000 with at most two decimals');
    if (!player) return reportError('Waiting for your balance');
    if (amount > player.balance) return reportError(notEnoughMessage(player.balance));
    const autoValue = parseAmountInput(autoCashoutMult);
    if (autoCashoutEnabled && (!Number.isFinite(autoValue) || autoValue < 1.01 || autoValue > 1000000 || Math.abs(autoValue * 100 - Math.round(autoValue * 100)) > 1e-7)) return reportError('Auto cashout must be between 1.01x and 1,000,000x');
    const gameId = gameInfo?.currentGame?.id;
    if (!gameId) return reportError('Waiting for the round');
    const actionId = await ActionIDManager.getNext(socket);
    socket.emit('game:bet', { amount, actionId, gameId, solo: soloMode, ...(autoCashoutEnabled ? { autoCashout: autoValue } : {}) });
  };

  const handleCashout = () => {
    const sent = emitIfConnected(socket, 'game:cashout', { gameId: gameInfo?.currentGame?.id });
    if (!sent) reportError('Connection lost — cash out not sent');
  };

  const adjustBet = (factor: number) => setBetAmount(value => String(Math.max(MIN_WAGER, Math.round((parseAmountInput(value) || 0) * factor * 100) / 100)));
  const phaseMessage = devSimulation?.active ? 'Local simulation — server actions disabled' : gameInfo?.phase === 'LAUNCHING' ? 'Dropping in…' : gameInfo?.phase === 'IN_PROGRESS' ? 'Round in progress' : gameInfo?.phase === 'CRASHED' ? 'Next run opening soon' : 'Waiting for the mountain';

  return (
    <div className="betting-controls" onFocus={onFieldFocus} onBlur={onFieldBlur}>
      <FriendsControls onSolo={returnToSolo} renderTrigger={openFriends => <ModeToggle soloMode={soloMode} locked={modeLocked || myBet.isActive} onChange={onSoloModeChange} onFriends={soloMode ? openFriends : undefined} />} />
      <div className="control-divider" />
      <div className="balance-row"><span>Available balance</span><strong>{formatMoney(player?.balance ?? 0)}</strong></div>
      <div className="control-divider" />

      <div className="wager-control">
        <label className="control-label" htmlFor="bet-amount">Wager</label>
        <div className="amount-field">
          <span>{DISPLAY_CONFIG.currencySymbol}</span><input id="bet-amount" {...decimalField} value={betAmount} onChange={e => setBetAmount(amountInputChars(e.target.value))} disabled={!canBet} aria-label="Bet amount" />
        </div>
        <div className="quick-bets">
          {[10, 50, 100, 500].map(value => <button key={value} onClick={() => setBetAmount(String(value))} disabled={!canBet}>{value}</button>)}
          <button onClick={() => adjustBet(.5)} disabled={!canBet}>½</button>
          <button onClick={() => adjustBet(2)} disabled={!canBet}>2×</button>
        </div>
      </div>

      <div className="auto-control">
        <div className="control-label-row">
          <div><span className="control-label">Auto cashout</span><p>Secure the run at a chosen multiplier.</p></div>
          <label className="toggle"><input type="checkbox" checked={autoCashoutEnabled} onChange={() => !myBet.isActive && setAutoEnabled(value => !value)} disabled={myBet.isActive} data-testid="auto-cashout-toggle" aria-label="Enable auto cashout" /><span className="toggle-slider" /></label>
        </div>
        {autoCashoutEnabled && <div className="auto-options">
          <div className="multiplier-field"><input aria-label="Auto cashout multiplier" {...decimalField} value={autoCashoutMult} onChange={e => setAutoMult(amountInputChars(e.target.value))} disabled={myBet.isActive} /><span>×</span></div>
          <div className="auto-presets">{['1.50', '2.00', '5.00'].map(value => <button key={value} onClick={() => setAutoMult(value)} disabled={myBet.isActive}>{value}×</button>)}</div>
          <div className="return-row"><span>Projected return</span><strong>{formatMoney(calculateReturn(amount, parseAmountInput(autoCashoutMult) || 0))}</strong></div>
        </div>}
      </div>

      {/* The action area: on phones it docks to the bottom of the screen so the
          wager / cash-out button stays in reach while the run is on screen. */}
      <div className={`bet-action${editingField ? ' is-undocked' : ''}`}>
      {error && <div className="control-error" role="alert">{error}</div>}
      {outOfCredits && <OutOfCreditsNotice />}

      {canBet && <button onClick={handleBet} className={`btn-primary ${autoCashoutEnabled ? 'btn-auto-bet' : 'btn-bet'}`}><span>{autoCashoutEnabled ? `Bet · auto ${autoCashoutMult}×` : 'Place wager'}</span><strong>{formatMoney(amount)}</strong></button>}
      {canCashout && <button onClick={handleCashout} className="btn-primary btn-cashout"><span>Cash out · {currentMultiplier.toFixed(2)}×</span><strong>{formatMoney(calculateReturn(activeBetAmount, currentMultiplier))}</strong></button>}

      {hasOpenLiveBet && !isOnline && <div className="bet-status is-pending" role="status"><strong>Reconnecting · cash out paused</strong><span>{formatMoney(activeBetAmount)}{autoCashoutEnabled ? ` · Auto ${autoCashoutMult}× still active` : ' still riding'}</span></div>}
      {myBet.isActive && !myBet.cashedOut && (gameInfo?.phase === 'STARTING' || gameInfo?.phase === 'LAUNCHING') && <div className="bet-status is-pending"><strong>{gameInfo.phase === 'LAUNCHING' ? 'Run launching' : 'Wager accepted'}</strong><span>{formatMoney(activeBetAmount)}{autoCashoutEnabled ? ` · Auto ${autoCashoutMult}×` : ''}</span></div>}
      {myBet.cashedOut && <div className="bet-status is-won"><strong>{autoCashoutTriggered ? 'Auto cashed out' : 'Cashed out'} · {shownCashoutMultiplier.toFixed(2)}×</strong><span>Return {formatMoney(calculateReturn(activeBetAmount, shownCashoutMultiplier))}</span></div>}
      {gameInfo?.phase === 'CRASHED' && myBet.isActive && !myBet.cashedOut && <div className="bet-status is-lost"><strong>Run lost</strong><span>{formatMoney(activeBetAmount)}</span></div>}
      {!canBet && !canCashout && !myBet.isActive && <div className="bet-status"><span>{phaseMessage}</span></div>}
      </div>
    </div>
  );
};

export default BettingControls;
